import { BoardState, Coordinate, Piece, PlayerColor, PieceType } from '../../../../../shared/types/game';
import { getPromotedPieceType } from './pieceUtils';

//指定された駒の、盤上での合法な移動先座標のリストを計算します。
export const getLegalMoves = (
  board: BoardState,
  from: Coordinate,
  piece: Piece,
  currentPlayerColor: PlayerColor
): Coordinate[] => {
  const legalMoves: Coordinate[] = [];
  const { row: startRow, col: startCol } = from;
  const { type, owner, isPromoted } = piece;

  // 自分の手番の駒でなければ移動できない
  if (owner !== currentPlayerColor) {
    return [];
  }

  // 盤面の範囲チェックヘルパー
  const isValidCoord = (r: number, c: number) => r >= 0 && r < 9 && c >= 0 && c < 9;

  // 移動先のチェックと追加ヘルパー
  const addMoveIfLegal = (r: number, c: number) => {
    if (!isValidCoord(r, c)) return false; // 盤外
    const targetPiece = board[r][c];
    if (targetPiece && targetPiece.owner === owner) {
      return false; // 自分の駒があるマスには移動できない
    }
    legalMoves.push({ row: r, col: c });
    return !targetPiece; // 駒がなければさらに移動できる可能性あり（飛び駒用）
  };

  // 飛び駒の移動をチェックするヘルパー
  const checkSlideMoves = (dr: number, dc: number) => {
    let r = startRow + dr;
    let c = startCol + dc;
    while (isValidCoord(r, c)) {
      const canContinue = addMoveIfLegal(r, c);
      if (!canContinue) break;
      r += dr;
      c += dc;
    }
  };

  // 駒の動きを定義
  // dr, dc は row, col の変化量
  // プレイヤーの向きに応じて dr を調整
  const direction = owner === 'sente' ? -1 : 1; // 先手は上方向(row減少)、後手は下方向(row増加)

  // 成り駒の場合の動き
  if (isPromoted) {
    const promotedType = getPromotedPieceType(type); // 成り駒の元のタイプから成った後のタイプを取得
    switch (promotedType) {
      case 'ryu': // 龍王 (飛車の成り)
        // 飛車の動き (縦横無制限)
        checkSlideMoves(1, 0); // 下
        checkSlideMoves(-1, 0); // 上
        checkSlideMoves(0, 1); // 右
        checkSlideMoves(0, -1); // 左
        // 王の動き (斜め1マス)
        addMoveIfLegal(startRow + 1, startCol + 1);
        addMoveIfLegal(startRow + 1, startCol - 1);
        addMoveIfLegal(startRow - 1, startCol + 1);
        addMoveIfLegal(startRow - 1, startCol - 1);
        break;
      case 'uma': // 龍馬 (角行の成り)
        // 角行の動き (斜め無制限)
        checkSlideMoves(1, 1); // 右下
        checkSlideMoves(1, -1); // 左下
        checkSlideMoves(-1, 1); // 右上
        checkSlideMoves(-1, -1); // 左上
        // 王の動き (縦横1マス)
        addMoveIfLegal(startRow + 1, startCol);
        addMoveIfLegal(startRow - 1, startCol);
        addMoveIfLegal(startRow, startCol + 1);
        addMoveIfLegal(startRow, startCol - 1);
        break;
      case 'tokin': // と金 (歩の成り)
      case 'narikyo': // 成香 (香車の成り)
      case 'narikei': // 成桂 (桂馬の成り)
      case 'narigin': // 成銀 (銀将の成り)
        // 金の動き (斜め後ろ以外1マス)
        addMoveIfLegal(startRow + direction, startCol); // 前
        addMoveIfLegal(startRow, startCol + 1); // 右
        addMoveIfLegal(startRow, startCol - 1); // 左
        addMoveIfLegal(startRow - direction, startCol); // 後ろ (先手なら下、後手なら上)
        addMoveIfLegal(startRow + direction, startCol + 1); // 前右
        addMoveIfLegal(startRow + direction, startCol - 1); // 前左
        break;
    }
  } else {
    // 元の駒の動き
    switch (type) {
      case 'fu': // 歩
        addMoveIfLegal(startRow + direction, startCol);
        break;
      case 'kyo': // 香車
        checkSlideMoves(direction, 0); // 前方無制限
        break;
      case 'kei': // 桂馬
        // 桂馬の特殊な動き（2マス前方に進み、左右どちらかに1マス移動）
        // プレイヤーの向きに応じてrowの変化量が異なる
        addMoveIfLegal(startRow + direction * 2, startCol + 1);
        addMoveIfLegal(startRow + direction * 2, startCol - 1);
        break;
      case 'gin': // 銀
        addMoveIfLegal(startRow + direction, startCol); // 前
        addMoveIfLegal(startRow + direction, startCol + 1); // 前右
        addMoveIfLegal(startRow + direction, startCol - 1); // 前左
        addMoveIfLegal(startRow - direction, startCol + 1); // 後ろ右 (先手なら下右、後手なら上右)
        addMoveIfLegal(startRow - direction, startCol - 1); // 後ろ左 (先手なら下左、後手なら上左)
        break;
      case 'kin': // 金
        // 金の動き (斜め後ろ以外1マス)
        addMoveIfLegal(startRow + direction, startCol); // 前
        addMoveIfLegal(startRow, startCol + 1); // 右
        addMoveIfLegal(startRow, startCol - 1); // 左
        addMoveIfLegal(startRow - direction, startCol); // 後ろ (先手なら下、後手なら上)
        addMoveIfLegal(startRow + direction, startCol + 1); // 前右
        addMoveIfLegal(startRow + direction, startCol - 1); // 前左
        break;
      case 'ou': // 王 (王将/玉将)
        // 王の動き (全方向1マス)
        addMoveIfLegal(startRow + 1, startCol);
        addMoveIfLegal(startRow - 1, startCol);
        addMoveIfLegal(startRow, startCol + 1);
        addMoveIfLegal(startRow, startCol - 1);
        addMoveIfLegal(startRow + 1, startCol + 1);
        addMoveIfLegal(startRow + 1, startCol - 1);
        addMoveIfLegal(startRow - 1, startCol + 1);
        addMoveIfLegal(startRow - 1, startCol - 1);
        break;
      case 'hisha': // 飛車
        checkSlideMoves(1, 0); // 下
        checkSlideMoves(-1, 0); // 上
        checkSlideMoves(0, 1); // 右
        checkSlideMoves(0, -1); // 左
        break;
      case 'kaku': // 角行
        checkSlideMoves(1, 1); // 右下
        checkSlideMoves(1, -1); // 左下
        checkSlideMoves(-1, 1); // 右上
        checkSlideMoves(-1, -1); // 左上
        break;
    }
  }

  return legalMoves;
};

//持ち駒を打つ際の合法なマス目の座標リストを計算
//二歩、打ち歩詰め、行きどころのない駒のルールを考慮する
export const getLegalDropMoves = (
  board: BoardState,
  pieceType: PieceType,
  owner: PlayerColor
): Coordinate[] => {
  const legalDropMoves: Coordinate[] = [];

  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      // 1. 空きマスであること
      if (board[r][c] !== null) {
        continue;
      }

      // 2. 二歩のチェック (歩の場合のみ)
      if (pieceType === 'fu') {
        let hasPawnInFile = false;
        for (let i = 0; i < 9; i++) {
          const piece = board[i][c];
          if (piece && piece.type === 'fu' && piece.owner === owner && !piece.isPromoted) {
            hasPawnInFile = true;
            break;
          }
        }
        if (hasPawnInFile) {
          continue; // その筋に自分の成っていない歩があるため、二歩
        }
      }

      // 3. 行きどころのない駒のチェック (歩、香車、桂馬の場合のみ)
      // 打った駒がそのマスに存在すると仮定した場合、その駒がその後動けるか
      if (pieceType === 'fu' || pieceType === 'kyo' || pieceType === 'kei') {
        const targetRow = r;
        let isNoLegalMoveAfterDrop = false;
        if (owner === 'sente') {
          if (pieceType === 'fu' && targetRow === 0) isNoLegalMoveAfterDrop = true;
          if (pieceType === 'kyo' && targetRow === 0) isNoLegalMoveAfterDrop = true;
          if (pieceType === 'kei' && (targetRow === 0 || targetRow === 1)) isNoLegalMoveAfterDrop = true;
        } else { // gote
          if (pieceType === 'fu' && targetRow === 8) isNoLegalMoveAfterDrop = true;
          if (pieceType === 'kyo' && targetRow === 8) isNoLegalMoveAfterDrop = true;
          if (pieceType === 'kei' && (targetRow === 8 || targetRow === 7)) isNoLegalMoveAfterDrop = true;
        }
        if (isNoLegalMoveAfterDrop) {
          continue; // 行きどころのないマスには打てない
        }
      }

      // 4. 打ち歩詰めのチェック (歩の場合のみ)
      // 複雑なので省略（いずれにせよサーバーサイドで打ち歩詰めはブロックされる

      legalDropMoves.push({ row: r, col: c });
    }
  }

  return legalDropMoves;
};